import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { MOVEMENT_STATUS_LABELS, cancelRemittance, declareRemittance, getPosition, listRemittances } from '@/data/remittances'
import type { MovementStatus, Remittance } from '@/data/remittances'
import { EXCEPTION_LABELS, listOpenExceptions, respondToException } from '@/data/reconciliation'
import type { ReconException } from '@/data/reconciliation'
import { preparePhoto } from '@/attachments/compress'
import type { PreparedPhoto } from '@/attachments/compress'
import { queueProofPhoto } from '@/attachments/receipts'
import { ProofPhotos } from '@/features/proofs/ProofPhotos'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { currentQuarterId, lusakaToday } from '@/lib/periods'
import { amountToNgwee, reasonSchema, remittanceFormSchema } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const newRef = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `x-${Date.now()}-${Math.random().toString(36).slice(2)}`)

const TONE: Record<MovementStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  declared: 'info',
  partially_settled: 'warning',
  settled: 'success',
  disputed: 'danger',
  cancelled: 'neutral',
}

export function RemittancesPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const qc = useQueryClient()
  const today = lusakaToday()
  const quarter = currentQuarterId()

  const listQ = useQuery({ queryKey: ['remittances', deptId], queryFn: () => listRemittances(deptId) })
  const posQ = useQuery({ queryKey: ['position', deptId, quarter], queryFn: () => getPosition(deptId, quarter) })

  const exQ = useQuery({ queryKey: ['exceptions', deptId], queryFn: () => listOpenExceptions(deptId) })
  const toast = useToast()
  const [replyTo, setReplyTo] = useState<ReconException | null>(null)
  const [reply, setReply] = useState('')
  const [replyError, setReplyError] = useState('')
  const replyM = useMutation({ mutationFn: (v: { id: string; text: string }) => respondToException(v.id, v.text) })
  const submitReply = async (e: FormEvent) => {
    e.preventDefault()
    if (!replyTo || replyM.isPending) return
    const p = reasonSchema.safeParse({ reason: reply })
    if (!p.success) return setReplyError('Write your explanation (at least a few words)')
    setReplyError('')
    try {
      await replyM.mutateAsync({ id: replyTo.id, text: p.data.reason })
      await qc.invalidateQueries({ queryKey: ['exceptions'] })
      setReplyTo(null)
      toast.success('Your explanation was sent to central treasury.')
    } catch (err) {
      setReplyError(errText(err))
    }
  }

  // ---------------- declare
  const [open, setOpen] = useState(false)
  const [fDate, setFDate] = useState(today)
  const [fAmount, setFAmount] = useState('')
  const [fMethod, setFMethod] = useState<PaymentMethod>('cash')
  const [fHandedTo, setFHandedTo] = useState('')
  const [fHandedBy, setFHandedBy] = useState('')
  const [fRef, setFRef] = useState('')
  const [fSlip, setFSlip] = useState('')
  const [fNote, setFNote] = useState('')
  const [fErrors, setFErrors] = useState<Record<string, string>>({})
  const [fError, setFError] = useState('')
  const [photos, setPhotos] = useState<PreparedPhoto[]>([])
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState('')
  const photoInput = useRef<HTMLInputElement>(null)
  const busy = useRef(false)
  const clientRef = useRef(newRef())

  const declareM = useMutation({ mutationFn: (v: { input: Parameters<typeof declareRemittance>[1]; ref: string }) => declareRemittance(deptId, v.input, v.ref) })

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['remittances', deptId] })
    await qc.invalidateQueries({ queryKey: ['position', deptId] })
  }

  const openForm = () => {
    setFDate(today); setFAmount(''); setFMethod('cash'); setFHandedTo(''); setFHandedBy(''); setFRef(''); setFSlip(''); setFNote('')
    setFErrors({}); setFError(''); setPhotos([]); setPhotoError('')
    clientRef.current = newRef()
    setOpen(true)
  }

  const pickPhotos = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setPhotoBusy(true)
    setPhotoError('')
    try {
      const prepared: PreparedPhoto[] = []
      for (const f of Array.from(files)) prepared.push(await preparePhoto(f))
      setPhotos((cur) => [...cur, ...prepared])
    } catch (e) {
      setPhotoError(errText(e))
    } finally {
      setPhotoBusy(false)
      if (photoInput.current) photoInput.current.value = ''
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    setFError('')
    const parsed = remittanceFormSchema.safeParse({
      movedOn: fDate, amount: fAmount, method: fMethod, handedTo: fHandedTo, handedBy: fHandedBy, bankRef: fRef, slipNo: fSlip, note: fNote,
    })
    const errs: Record<string, string> = {}
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
    if (parsed.success && parsed.data.movedOn > today) errs.movedOn = 'The date cannot be in the future'
    setFErrors(errs)
    if (!parsed.success || Object.keys(errs).length > 0) return
    const v = parsed.data
    busy.current = true
    try {
      const id = await declareM.mutateAsync({
        ref: clientRef.current,
        input: {
          amountNgwee: amountToNgwee(v.amount) as number, method: v.method, movedOn: v.movedOn, bankRef: v.bankRef, slipNo: v.slipNo,
          handedTo: v.handedTo, handedBy: v.handedBy, note: v.note,
        },
      })
      // Saved. The declaration is on the record from here on, whatever happens next, so nothing after
      // this point may keep the form open (a second tap would only return the same declaration).
      // The photos now go on the on-device queue and upload in the background.
      let note = ''
      try {
        for (const p of photos) await queueProofPhoto(p, { departmentId: deptId, parent: { kind: 'movement', id }, date: v.movedOn })
      } catch (err) {
        note = ` The photo could not be saved on this device (${errText(err)}). Add it again from the list below.`
      }
      setPhotos([])
      await refresh().catch(() => undefined)
      setOpen(false)
      toast.success(`The remittance was recorded. Central treasury will confirm when they receive it.${note}`)
    } catch (err) {
      setFError(errText(err))
    } finally {
      busy.current = false
    }
  }

  // ---------------- cancel
  const [cancelRow, setCancelRow] = useState<Remittance | null>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [cancelError, setCancelError] = useState('')
  const cancelM = useMutation({ mutationFn: (v: { id: string; reason: string }) => cancelRemittance(v.id, v.reason) })

  const submitCancel = async (e: FormEvent) => {
    e.preventDefault()
    if (!cancelRow || cancelM.isPending) return
    const p = reasonSchema.safeParse({ reason })
    if (!p.success) return setReasonError(p.error.issues[0].message)
    setReasonError('')
    setCancelError('')
    try {
      await cancelM.mutateAsync({ id: cancelRow.id, reason: p.data.reason })
      await refresh()
      setCancelRow(null)
      toast.success('The remittance was cancelled. It stays on the record.')
    } catch (err) {
      setCancelError(errText(err))
      void refresh()
    }
  }

  const rows = listQ.data ?? []
  const pos = posQ.data

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Money sent to central treasury</h2>
        <p className="mt-1 text-muted">Record cash or deposits you send. Central treasury confirms them when the money arrives.</p>
      </div>


      {(exQ.data ?? []).length > 0 && (
        <Card>
          <CardHeader><h3 className="font-semibold text-ink">Questions from central treasury</h3></CardHeader>
          <CardBody>
            <ul className="divide-y divide-line">
              {(exQ.data ?? []).map((x) => (
                <li key={x.id} className="py-3">
                  <p className="font-medium text-ink">{EXCEPTION_LABELS[x.kind]}{x.varianceNgwee !== null ? ` · ${formatKwacha(x.varianceNgwee)}` : ''}</p>
                  {x.deptResponse && <p className="text-body text-neutral">You said: {x.deptResponse}</p>}
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => { setReply(x.deptResponse ?? ''); setReplyError(''); setReplyTo(x) }}
                      className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-brand hover:bg-neutral-tint"
                    >
                      {x.deptResponse ? 'Change your explanation' : 'Explain'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {pos && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Cash position">
          {[
            ['Collected this quarter', pos.collectedNgwee],
            ['Spent from cash', pos.spentLocalNgwee],
            ['Sent to central', pos.remittedNgwee],
            ['Cash you should hold', pos.cashOnHandNgwee],
          ].map(([label, value]) => (
            <Card key={String(label)}>
              <CardBody>
                <p className="text-body text-muted">{label}</p>
                <p className="text-title font-semibold text-ink">{formatKwacha(value as number)}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Remittances</h3>
          {canWrite && <Button size="lg" onClick={openForm}>Record money sent</Button>}
        </CardHeader>
        <CardBody>
          {listQ.isPending ? (
            <SkeletonRows rows={4} />
          ) : listQ.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(listQ.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void listQ.refetch()}>Try again</Button>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title="Nothing sent yet" description={canWrite ? 'Record it here each time you hand over or deposit money.' : 'Nothing has been recorded.'} />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{formatDay(r.movedOn)} · {METHOD_LABELS[r.method]}</p>
                      <p className="text-body text-muted">
                        {r.method === 'cash'
                          ? `Handed to ${r.handedTo}${r.handedBy ? `, by ${r.handedBy}` : ''}`
                          : `Reference ${r.bankRef ?? 'none'}${r.slipNo ? `, slip ${r.slipNo}` : ''}`}
                      </p>
                      {r.note && <p className="text-micro text-muted">{r.note}</p>}
                      {r.status !== 'cancelled' && r.matchedNgwee > 0 && (
                        <p className="text-body text-neutral">Confirmed {formatKwacha(r.matchedNgwee)} of {formatKwacha(r.declaredNgwee)}</p>
                      )}
                      {r.status === 'cancelled' && <p className="text-body text-muted">Cancelled: {r.cancelReason}</p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="font-medium text-ink">{formatKwacha(r.declaredNgwee)}</span>
                      <Badge tone={TONE[r.status]}>{MOVEMENT_STATUS_LABELS[r.status]}</Badge>
                    </div>
                  </div>
                  {r.status !== 'cancelled' && (
                    <ProofPhotos parent={{ kind: 'movement', id: r.id }} departmentId={r.departmentId} date={r.movedOn} canAdd={canWrite} noun="deposit slip" />
                  )}
                  <OwnerDeleteButton table="cash_movement" id={r.id} what="remittance" wrap="mt-2 flex flex-wrap gap-2" />
                  {canWrite && r.status === 'declared' && (
                    <button
                      type="button"
                      onClick={() => { setCancelError(''); setReason(''); setReasonError(''); setCancelRow(r) }}
                      className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-bad hover:bg-neutral-tint"
                    >
                      Cancel this
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={open} onClose={() => setOpen(false)} title="Record money sent">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Date sent" type="date" max={today} value={fDate} onChange={(e) => setFDate(e.target.value)} error={fErrors.movedOn} />
          <Input label="Amount in Kwacha" inputMode="decimal" placeholder="500.00" value={fAmount} onChange={(e) => setFAmount(e.target.value)} error={fErrors.amount} />
          <Select
            label="How was it sent?"
            value={fMethod}
            onChange={(e) => setFMethod(e.target.value as PaymentMethod)}
            options={(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD_LABELS[m] }))}
          />
          {fMethod === 'cash' ? (
            <>
              <Input label="Who received the cash?" value={fHandedTo} onChange={(e) => setFHandedTo(e.target.value)} error={fErrors.handedTo} />
              <Input label="Who handed it over? (optional)" value={fHandedBy} onChange={(e) => setFHandedBy(e.target.value)} error={fErrors.handedBy} />
            </>
          ) : (
            <>
              <Input label="Bank or mobile money reference" value={fRef} onChange={(e) => setFRef(e.target.value)} error={fErrors.bankRef} hint="From the deposit slip or the message. Central treasury uses it to match the money." />
              <Input label="Slip number (optional)" value={fSlip} onChange={(e) => setFSlip(e.target.value)} error={fErrors.slipNo} />
            </>
          )}
          <Input label="Note (optional)" value={fNote} onChange={(e) => setFNote(e.target.value)} error={fErrors.note} />
          <div className="flex flex-col gap-1.5">
            <span className="text-body font-medium text-neutral">Photo of the deposit slip or handover (optional)</span>
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="sr-only"
              aria-label="Photo of the deposit slip"
              onChange={(e) => void pickPhotos(e.target.files)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" size="lg" disabled={photoBusy} onClick={() => photoInput.current?.click()}>
                {photoBusy ? 'Preparing photo...' : photos.length === 0 ? 'Take or choose a photo' : 'Add another photo'}
              </Button>
              {photos.length > 0 && (
                <span className="text-body text-neutral">
                  {photos.length} {photos.length === 1 ? 'photo' : 'photos'} ready
                  <button type="button" className="ml-2 inline-flex min-h-[44px] items-center px-2 text-body font-medium text-bad" onClick={() => setPhotos([])}>Remove</button>
                </span>
              )}
            </div>
            <span className="text-body text-muted">The record is saved first. The photo uploads in the background.</span>
            {photoError && <span role="alert" className="text-body text-bad">{photoError}</span>}
          </div>
          {fError && <p role="alert" className="text-body text-bad">{fError}</p>}
          <Button type="submit" size="lg" isLoading={declareM.isPending}>{declareM.isPending ? 'Saving...' : 'Save'}</Button>
        </form>
      </Sheet>

      <Sheet isOpen={replyTo !== null} onClose={() => setReplyTo(null)} title="Explain to central treasury">
        <form onSubmit={(e) => void submitReply(e)} className="flex flex-col gap-4" noValidate>
          <p className="text-neutral">{replyTo ? EXCEPTION_LABELS[replyTo.kind] : ''}. Only central treasury can close this; your note helps them.</p>
          <Textarea label="What happened?" value={reply} onChange={(e) => setReply(e.target.value)} error={replyError} rows={4} />
          <Button type="submit" size="lg" isLoading={replyM.isPending}>Send explanation</Button>
        </form>
      </Sheet>

      <Sheet isOpen={cancelRow !== null} onClose={() => setCancelRow(null)} title="Cancel this remittance?">
        <form onSubmit={(e) => void submitCancel(e)} className="flex flex-col gap-4" noValidate>
          <p className="text-neutral">
            {cancelRow ? `${formatKwacha(cancelRow.declaredNgwee)} on ${formatDay(cancelRow.movedOn)}. ` : ''}It stays on the record, marked cancelled.
          </p>
          <Textarea label="Why?" value={reason} onChange={(e) => setReason(e.target.value)} error={reasonError} rows={3} />
          {cancelError && <p role="alert" className="text-body text-bad">{cancelError}</p>}
          <Button type="submit" size="lg" variant="danger" isLoading={cancelM.isPending}>Cancel remittance</Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => setCancelRow(null)}>Keep it</Button>
        </form>
      </Sheet>
    </div>
  )
}
